import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";

import { useSelector, useDispatch } from "react-redux";
import { message } from "antd";

import { CounselorAction, MeetingAction } from "../../store/actions";

import {
  meeting,
  boxContainer,
  Spacer,
  inputFields,
  Loader,
  datepicker,
  upload,
  Buttons,
} from "../../components";
import { Images, Utils } from "../../config";

const RescheduleMeeting = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const profileData = useSelector((state) => state.profile?.user);
  const { counselors, isLoading } = useSelector((state) => state.counselors);
  const meetingLoading = useSelector((state) => state.meeting?.isLoading);
  const timeslots = useSelector((state) => state.meeting?.timeslots);
  const timeslotLoading = useSelector(
    (state) => state.meeting?.isTimeslotLoading
  );

  const [buttonText, setButtonText] = useState("Reschedule");
  const [counselorList, setCounselorList] = useState([]);
  const [timeSlot, setTimeSlot] = useState([]);

  const [state, setState] = useState({
    meetingId: "",
    subject: "",
    meetingDate: null,
    meetingDatePrev: "",
    timeSlot: null,
    timeSlotPrev: null,
    message: "",
    meetingType: "",
    counselorUserId: null,
    meetingLink: "",
    meetingFile: "",
  });

  useEffect(() => {
    setState({
      ...state,
      meetingId: location?.state?.id,
      subject: location?.state?.subject,
      meetingDate: location?.state?.meetingDate,
      meetingDatePrev: location?.state?.meetingDate,
      timeSlot: location?.state?.timeSlot?.id,
      timeSlotPrev: location?.state?.timeSlot?.id,
      message: location?.state?.message,
      meetingType: location?.state?.meetingType,
      counselorUserId: location?.state?.counselorUserId,
      meetingLink: location?.state?.meetingLink,
      meetingFile: location?.state?.meetingFile,
    });
  }, [location?.state]);

  useEffect(() => {
    if (counselors.length === 0) {
      const payload = {
        id: profileData?.student?.schoolId,
      };
      dispatch(CounselorAction.getCounselors(payload));
    }
  }, [profileData]);

  useEffect(() => {
    if (profileData?.id && state?.meetingDate && state?.counselorUserId) {
      const payload = {
        userId: state?.counselorUserId,
        date: state?.meetingDate,
      };
      dispatch(MeetingAction.getTimeslots(payload));
    }
  }, [state?.meetingDate, state?.counselorUserId]);

  useEffect(() => {
    const filteredTimeslots = timeslots.map((timeslot) => ({
      label: timeslot?.timeSlot,
      value: timeslot?.id,
    }));
    setTimeSlot(filteredTimeslots);
  }, [timeslots]);

  useEffect(() => {
    const filteredCounselors = counselors.map((counselor) => ({
      label: counselor?.user
        ? `${counselor?.user?.firstName} ${counselor?.user?.lastName}`
        : `${counselor?.userId?.firstName} ${counselor?.userId?.lastName}`,
      value: counselor?.user ? counselor?.user?.id : counselor?.userId?.id,
    }));
    setCounselorList(filteredCounselors);
  }, [counselors]);

  const handleSubmit = () => {
    if (
      !state?.meetingDate ||
      !state?.timeSlot ||
      !state?.meetingType ||
      !state?.meetingLink
    ) {
      message.error("Please fill all the required fields.");
    } else if (
      state?.meetingType === "online" &&
      state?.meetingLink &&
      !Utils?.isValidURL(state.meetingLink)
    ) {
      message.error("Please enter a valid meeting link.");
    } else {
      setButtonText("Rescheduling");
      const payload = {
        meetingId: state?.meetingId,
        timeSlot: state?.timeSlot,
        timeSlotPrev: state?.timeSlotPrev,
        meetingType: state?.meetingType,
        meetingLink: state?.meetingLink,
        meetingDate: state?.meetingDate,
        meetingDatePrev: state?.meetingDatePrev,
        message: state?.message,
      };

      dispatch(
        MeetingAction.rescheduleMeeting(payload, (res) => {
          if (res.status === 200) {
            navigate("/meetings/received");
          }
          setButtonText("Reschedule");
        })
      );
    }
  };

  const radioItems = [
    { label: "In Person", value: "offline" },
    { label: "Online", value: "online" },
  ];

  return (
    <div className="text-black">
      <div className="header-md pl-3">Reschedule Meeting</div>
      <Spacer height={20} />
      <boxContainer.LiteBoxContainer>
        <div className="text-[1.25em] font-semibold">
          Fill in your details and schedule a meeting for any guidance regarding
          admissions from your College counsellor.
        </div>
        <Spacer height={25} />
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2 opacity-50 pointer-events-none">
            <meeting.AddMeetingBoxLabel
              title="Select Counselor"
              required={true}
            />
            {isLoading ? (
              <Loader.Circular />
            ) : (
              <inputFields.SelectDropdown
                items={counselorList}
                value={state?.counselorUserId}
              />
            )}
          </div>
          <div className="flex flex-col gap-2 opacity-50 pointer-events-none">
            <meeting.AddMeetingBoxLabel title="Subject" required={true} />
            <inputFields.DefaultInput value={state.subject} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="flex flex-col gap-2">
              <meeting.AddMeetingBoxLabel title="Select Date" required={true} />
              <datepicker.DefaultDatepicker
                datevalue={state?.meetingDate}
                style={{ height: "3em" }}
                onChange={(date, dateString) =>
                  setState({
                    ...state,
                    meetingDate: dateString,
                    timeSlot: null,
                  })
                }
              />
            </div>
            <div className="flex flex-col gap-2">
              <meeting.AddMeetingBoxLabel
                title="Select Timeslot"
                required={true}
              />
              <inputFields.SelectDropdown
                items={timeSlot}
                onChange={(e) => setState({ ...state, timeSlot: e })}
                style={{ height: "3em" }}
                value={state?.timeSlot}
                placeholder="Select Time Slot"
                loading={timeslotLoading}
              />
            </div>
            <div className="flex flex-col gap-2">
              <meeting.AddMeetingBoxLabel title="Document Attachment" />
              {state.meetingFile ? (
                <Link to={state?.meetingFile} target="_blank">
                  <div className="default-file-input">
                    <div className={`opacity-[100%] text-[14px]`}>
                      File uploaded
                    </div>
                    <div>
                      <img
                        src={Images.DocumentAttachment}
                        className="h-[18px] pr-2"
                      />
                    </div>
                  </div>
                </Link>
              ) : (
                <upload.DefaultFileUpload
                  onChange={(file) => setState({ ...state, meetingFile: file })}
                  value={state.meetingFile}
                  icon={Images.DocumentAttachment}
                />
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <meeting.AddMeetingBoxLabel title="Message" />
            <inputFields.DefaultTextbox
              onChange={(e) => setState({ ...state, message: e.target.value })}
              value={state.message}
            />
          </div>
          <div className="flex flex-col gap-2">
            <meeting.AddMeetingBoxLabel
              title="Select Mode of Meeting"
              required={true}
            />
            <div className="flex gap-[100px] items-center">
              <div className="pl-2">
                <inputFields.VerticalRadio
                  items={radioItems}
                  onChange={(e) =>
                    setState({ ...state, meetingType: e.target.value })
                  }
                  value={state.meetingType}
                />
              </div>
              {state.meetingType && (
                <div className="flex flex-col gap-2 w-[500px]">
                  <meeting.AddMeetingBoxLabel
                    title={
                      state.meetingType === "offline"
                        ? "Add Location"
                        : "Attach Link"
                    }
                    required={true}
                  />
                  <inputFields.DefaultInput
                    onChange={(e) =>
                      setState({ ...state, meetingLink: e.target.value })
                    }
                    value={state.meetingLink}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <Spacer height={30} />
        <div className="flex w-full justify-end">
          <Buttons.DefaultButton
            disabled={meetingLoading}
            onClick={handleSubmit}
            loading={meetingLoading}
          >
            {buttonText}
          </Buttons.DefaultButton>
        </div>
      </boxContainer.LiteBoxContainer>
    </div>
  );
};

export default RescheduleMeeting;
